import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FIELDS,
  LOADING,
  MODE,
  TOAST_MESSAGES,
} from "../../../constants/SiteConstants";
import VendorForm from "./VendorForm";
import { useJsApiLoader } from "@react-google-maps/api";
import AWS from "../../../utils/awsConfig.js";

const API_URL = process.env.REACT_APP_API_URL;

const fields = [
  {
    label: FIELDS.NAME,
    name: "name",
    placeholder: "Enter vendor name",
    required: true,
  },
  {
    label: "Enable or Disable ",
    name: "isActive",
    type: "checkbox",
  },
  {
    label: "OwnerName",
    name: "ownerName",
    placeholder: "Enter owner name",
    required: true,
  },
  {
    label: "Head Office Location",
    name: "location",
    placeholder: "Enter head office address",
    required: true,
  },
  {
    name: "state",
    placeholder: "Enter State name",
    required: true,
    isMultifield: true,
  },
  {
    name: "city",
    placeholder: "Enter City name",
    required: true,
    isMultifield: true,
  },
  {
    label: "Branch Address",
    name: "branchAddress",
    placeholder: "Enter branch address",
    required: true,
  },
  {
    label: FIELDS.EMAIL,
    name: "email",
    placeholder: "Enter email address",
    required: true,
  },
  {
    label: "Sponsored",
    name: "sponsored",
    type: "checkbox",
  },
  {
    label: "ApprovedByGovernment",
    name: "approvedByGovernment",
    type: "checkbox",
  },
  {
    label: "ApprovedBySolarStation",
    name: "approvedBySolarStation",
    type: "checkbox",
  },
  {
    label: "Verified",
    name: "verified",
    type: "checkbox",
  },
  {
    label: FIELDS.RANKING,
    name: "ranking",
    placeholder: "Enter ranking",
    required: true,
  },
  {
    label: FIELDS.PHONE,
    name: "phone",
    placeholder: "Enter phone number",
    required: true,
  },
  {
    label: FIELDS.DESCRIPTION,
    name: "description",
    placeholder: "Enter vendor description",
    required: true,
    type: "rich-text",
  },
  {
    label: FIELDS.IMAGE,
    name: "image",
    placeholder: "Enter image",
    type: "file",
  },
  {
    label: FIELDS.TAGS,
    name: "tags",
    placeholder: "Enter tags (comma separated)",
    isMultifield: true,
  },
  {
    label: FIELDS.SLUG,
    name: "slug",
    placeholder: "Enter slug",
    required: true,
  },
];

const fetchVendorById = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/vendor/getVendor/${id}`);
    return response.data.data;
  } catch (error) {
    throw new Error("Failed to load vendor data. Please try again.");
  }
};

const handleImageUpload = async (file) => {
  if (!file) {
    toast.error("No file selected!");
    return null;
  }

  const formData = new FormData();
  formData.append("image", file);

  try {
    const response = await fetch(`${API_URL}/image/upload`, {
      method: "POST",
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "Upload failed");
    }

    return data.imageUrl;
  } catch (error) {
    console.error("Upload Error:", error);
    toast.error("Error uploading image: " + error.message);
    return null;
  }
};


const EditVendor = () => {
  const { id } = useParams();
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fileInputs, setFileInputs] = useState({
    image: null,
    customerImages: null,
  });
  const [previewImages, setPreviewImages] = useState({
    customerImages: [],
    image: [],
  });
  const navigate = useNavigate();

  const loaderOptions = useMemo(
    () => ({
      id: "google-map-script",
      googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
      libraries: ["places"],
    }),
    []
  );

  const { isLoaded } = useJsApiLoader(loaderOptions);

  useEffect(() => {
    const loadVendorData = async () => {
      try {
        const data = await fetchVendorById(id);

        const normalizedData = {
          ...data,
          state: data?.state || "",
          city: data?.city || "",
          branchAddress: data?.branches?.[0]?.location?.address || "",
        };

        setInitialData(normalizedData);

        setPreviewImages({
          customerImages: data.customerImages || [],
          image: data.image || [],
        });
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadVendorData();
    }
  }, [id]);

  const handleSubmit = async (formData) => {
    const loggedInUser = JSON.parse(localStorage.getItem("user"));
    const userId = loggedInUser?._id;

    try {
      if (fileInputs.image) {
        const uploadedImageUrl = await handleImageUpload(fileInputs.image);
        formData.image = uploadedImageUrl;
      }

      if (fileInputs.customerImages) {
        const uploadedCustomerImageUrl = await handleImageUpload(fileInputs.customerImages);
        formData.customerImages = uploadedCustomerImageUrl;
      }

      // Handle branches update (optional)
      if (formData.branchAddress) {
        formData.branches = [
          {
            location: {
              address: formData.branchAddress,
              lat: initialData?.branches?.[0]?.location?.lat || 0,
              lng: initialData?.branches?.[0]?.location?.lng || 0,
            },
            phone: formData.phone,
          },
        ];
      }

      await axios.put(`${API_URL}/vendor/updateVendor/${id}`, {
        ...formData,
        userId: userId,
      });

      toast.success(TOAST_MESSAGES.DATA_UPDATED);
      navigate("/vendors");
    } catch (error) {
      toast.error(TOAST_MESSAGES.ERROR_UPDATE);
    }
  };

  const handleFileChange = (event, fieldName) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      setFileInputs((prev) => ({
        ...prev,
        [fieldName]: files[0],
      }));

      setPreviewImages((prev) => ({
        ...prev,
        [fieldName]: files.map((file) => URL.createObjectURL(file)),
      }));
    }
  };

  return (
    <div className="form-wrapper shadow-md">
      <div className="form-header">
        <h3 className="font-medium text-black border-b border-gray-300 pb-2 w-full">
          Update Vendor
        </h3>
        <button className="form-close-btn" onClick={() => navigate("/vendors")}>
          &times;
        </button>
      </div>

      {loading ? (
        <p>{LOADING}</p>
      ) : (
        initialData && (
          <VendorForm
            mode={MODE.UPDATE}
            onSubmit={handleSubmit}
            fields={fields}
            initialData={initialData}
            handleImageChange={(e) => handleFileChange(e, "image")}
            handleCustomerImageChange={(e) => handleFileChange(e, "customerImages")}
            imagePreviews={previewImages.image}
            customerImagePreviews={previewImages.customerImages}
            isGoogleMapLoaded={isLoaded}
          />
        )
      )}
    </div>
  );
};

export default EditVendor;
